import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

/*
 * Two namespaces per language:
 *
 *   translation  the app's chrome — sidebars, top bar, settings
 *   exam         every screen a candidate sees on exam day (W4.2): the
 *                lobby, pre-checks, consent chrome, the paper's chrome,
 *                warnings, review-before-submit, the result, the phone pages.
 *                The questions themselves are not here: they are the
 *                institution's, translated per question (3.7, paperLanguage.js).
 *
 * English is the source. Every other file carries `_meta.reviewed`: false
 * until a native reader has signed the wording off. The exam screens show a
 * language only when the institution has enabled it (Setup › Exam Screen
 * Languages, backend/config/screenLanguages.js), and the console marks an
 * unreviewed one "needs review" — see src/i18n/examLanguage.js.
 * src/i18n/__tests__/examScreens.test.js keeps every locale's keys equal to
 * English's and every exam-screen string going through t().
 */
import en from "./locales/en.json";
import hi from "./locales/hi.json";
import or from "./locales/or.json";
import bn from "./locales/bn.json";
import ta from "./locales/ta.json";
import te from "./locales/te.json";
import mr from "./locales/mr.json";
import kn from "./locales/kn.json";

import examEn from "./locales/exam/en.json";
import examHi from "./locales/exam/hi.json";
import examOr from "./locales/exam/or.json";
import examBn from "./locales/exam/bn.json";
import examTa from "./locales/exam/ta.json";
import examTe from "./locales/exam/te.json";
import examMr from "./locales/exam/mr.json";
import examKn from "./locales/exam/kn.json";

export const LOCALES = {
  en: { translation: en, exam: examEn },
  hi: { translation: hi, exam: examHi },
  or: { translation: or, exam: examOr },
  bn: { translation: bn, exam: examBn },
  ta: { translation: ta, exam: examTa },
  te: { translation: te, exam: examTe },
  mr: { translation: mr, exam: examMr },
  kn: { translation: kn, exam: examKn },
};

// The order a picker lists them: English, then the first wave (Hindi, Odia,
// Bengali), then the second (Tamil, Telugu, Marathi, Kannada).
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "or", label: "Odia", native: "ଓଡ଼ିଆ" },
  { code: "bn", label: "Bengali", native: "বাংলা" },
  { code: "ta", label: "Tamil", native: "தமிழ்" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
  { code: "mr", label: "Marathi", native: "मराठी" },
  { code: "kn", label: "Kannada", native: "ಕನ್ನಡ" },
].map((l) => ({
  ...l,
  // A language a native reader has signed off (its exam file's _meta).
  reviewed: l.code === "en" || LOCALES[l.code].exam?._meta?.reviewed === true,
}));

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: LOCALES,
    ns: ["translation", "exam"],
    defaultNS: "translation",
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "ux-locale",
    },
    react: { useSuspense: false },
  });

// <html lang> follows the language: the browser then picks the font for the
// script (fonts.css), a screen reader the right voice, and line breaking the
// right rules.
const markDocument = (lng) => {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("lang", String(lng || "en").split("-")[0]);
  }
};
markDocument(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", markDocument);

export default i18n;
